<script lang="ts">
  import { m } from "#messages";
  import { getLocale, publicHref } from "#lib/locale.ts";
  import { CONTACT_EMAIL } from "#lib/routes.ts";
  import { localeOptions, pickLocale } from "#lib/shell/account.ts";
  import Segmented from "#lib/ui/Segmented.svelte";
  import Wordmark from "#lib/ui/Wordmark.svelte";
  import { ENTRANCE_FRAME } from "#lib/ui/recipes.ts";
  import FloorField from "./FloorField.svelte";
  import { FEATURED, LEGAL, PRODUCTS, RESOURCES, WORKSPACE } from "./products.ts";

  interface FooterLink {
    readonly href: string;
    readonly label: string;
    readonly away?: boolean;
  }

  const DISLACE_HREF = "https://dislace.com";

  const COLUMNS: readonly { readonly title: string; readonly links: readonly FooterLink[] }[] =
    $derived([
      {
        title: m.footer_product(),
        links: [...PRODUCTS, WORKSPACE, ...FEATURED].map((link) => ({
          href: link.href,
          label: link.label(),
        })),
      },
      {
        title: m.footer_resources(),
        links: RESOURCES.map((link) => ({
          href: link.href,
          label: link.label(),
          away: link.away === true,
        })),
      },
      {
        title: m.footer_legal(),
        links: LEGAL.map((link) => ({ href: link.href, label: link.label() })),
      },
    ]);

  const locales = $derived(localeOptions().map(({ id, label }) => ({ value: id, label })));
</script>

<footer class="relative mt-16 overflow-hidden" data-testid="entrance-footer">
  <div class="absolute inset-x-0 bottom-0 h-112"><FloorField testId="footer-reading" /></div>
  <div class={[ENTRANCE_FRAME, "relative"]}>
    <div class="flex flex-col gap-12 pt-12 pb-72">
      <div class="grid gap-10 sm:grid-cols-3 md:flex md:justify-between">
        <div class="flex flex-col gap-4 sm:col-span-full">
          <span class="flex h-6 items-center text-ink-1"><Wordmark height={13} /></span>
          <a
            href={DISLACE_HREF}
            rel="noopener"
            class="inline-flex w-fit items-center gap-1.5 text-md text-ink-2 transition-colors duration-120 hover:text-ink-1"
            data-testid="footer-dislace"
          >
            {m.footer_run_by()}
            <img src="/dislace.svg" alt="" class="size-4 rounded-sm" />
            <span class="font-medium text-ink-1">{m.footer_company()}</span>
          </a>
        </div>
        {#each COLUMNS as column (column.title)}
          <nav class="flex flex-col gap-3" aria-label={column.title}>
            <h2 class="text-md font-medium text-ink-1">
              {column.title}
            </h2>
            <ul class="flex flex-col gap-2">
              {#each column.links as link (link.href)}
                <li>
                  <a
                    href={link.away === true ? link.href : publicHref(link.href)}
                    class="inline-flex min-h-6 items-center text-md text-ink-2 transition-colors duration-120 hover:text-ink-1"
                    >{link.label}</a
                  >
                </li>
              {/each}
            </ul>
          </nav>
        {/each}
      </div>
      <div class="flex flex-wrap items-center justify-between gap-x-10 gap-y-4 text-sm text-ink-3">
        <span class="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span>{m.footer_copyright()}</span>
          <a
            href="mailto:{CONTACT_EMAIL}"
            class="transition-colors duration-120 hover:text-ink-1"
            data-testid="footer-email">{CONTACT_EMAIL}</a
          >
        </span>
        {#if locales.length > 1}
          <Segmented
            look="pill"
            options={locales}
            value={getLocale()}
            onchange={pickLocale}
            ariaLabel={m.menu_language()}
            testId="footer-language"
          />
        {/if}
      </div>
    </div>
  </div>
</footer>
